import { useParams } from 'react-router-dom';
import { useDataRoomQuery, useRelatedDealsQuery } from '../shared/hooks';
import {
	CerebroBreadcrumb,
	CerebroForm,
	CerebroHeading,
	CerebroLabel,
	CerebroLink,
	CerebroPageContainer,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroText,
	CerebroYesNo,
} from '../shared/components';
import {
	Box,
	Grid,
	Skeleton,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from '@mui/material';
import { DateUtils, EnumUtils, FormikUtils, NumberUtils } from '../shared/utils';
import { LoanApplicationUrl, PlatformRootUrl } from '../shared/utils/platform';
import { DealSearchItem, SimilarDealSummary } from '../shared/models';
import { useState } from 'react';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useMutation } from 'react-query';
import { getApiResponseData } from '../shared/utils/axios';
import { CerebroApiEndpoints } from '../shared/utils/api';
import { useApi } from '../shared/context';
import { useSnackbar } from 'notistack';
import { Field, Formik, FormikErrors } from 'formik';

export const RelatedDeals: React.FC = () => {
	const api = useApi();
	const snackbar = useSnackbar();
	const { guid } = useParams<'guid'>();
	const [tab, setTab] = useState<string>('matches');
	const [downloadReport, setDownloadReport] = useState<boolean>(false);

	const dataRoomQuery = useDataRoomQuery(guid);
	const relatedQuery = useRelatedDealsQuery(dataRoomQuery.data?.dealGuid ?? '', {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});
	const handleTabChange = (event: any, newValue: string) => {
		setTab(newValue);
	};
	const [reportGenerating, setReportGenerating] = useState(false);

	const reportMutation = useMutation({
		mutationFn: async () => {
			setReportGenerating(true);
			const { data, error } = await getApiResponseData<SimilarDealsReportResponse>(
				api.post(CerebroApiEndpoints.RelatedDealsReport(dataRoomQuery.data?.dealGuid))
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		onSuccess: data => {
			setReportGenerating(false);
			window.location.href = `${PlatformRootUrl}/Emp/CustomTermSheetReport/${data?.termSheetReportHeaderGuid}`;
		},
		onError: (error: Error) => {
			snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			setReportGenerating(false);
		},
	});

	const downloadMutation = useMutation(
		async ({ values }: DownloadSimilarDealsMutationVariables) => {
			setDownloadReport(true);
			location.href = `${CerebroApiEndpoints.RelatedDownload(dataRoomQuery.data?.dealGuid)}?includeSummary=${
				values.includeSummary
			}&includeIndustry=${values.includeIndustry}&includeOther=${values.includeOther}`;
			return null;
		},
		{
			onSuccess: () => {},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem downloading the report.', { variant: 'error' });
			},
			onSettled: () => {
				setDownloadReport(false);
			},
		}
	);

	return (
		<CerebroPageContainer maxWidth="100%">
			<CerebroBreadcrumb>Similar Deals</CerebroBreadcrumb>

			{relatedQuery.isSuccess && (
				<SimilarSummaryTable
					matched={relatedQuery.data.matchedSummary}
					other={relatedQuery.data.otherSummary}
				/>
			)}

			<TabContext value={tab}>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<TabList onChange={handleTabChange}>
						<Tab label="Industry Matches" value="matches" />
						<Tab label="Other Industry Matches" value="other" />
						<Tab label="Report Actions & Info" value="info" />
					</TabList>
				</Box>

				{relatedQuery.isLoading && <Skeleton height={300} />}

				{relatedQuery.isSuccess && (
					<>
						<TabPanel value="matches">
							<SimilarDealTable items={relatedQuery.data.items.filter(item => item.industryMatch == 1)} />
						</TabPanel>
						<TabPanel value="other">
							<SimilarDealTable items={relatedQuery.data.items.filter(item => item.industryMatch == 0)} />
						</TabPanel>
						<TabPanel value="info">
							<CerebroText sx={{ padding: 3 }}>
								These deals have similar characteristics to this deal. The deals listed below have
								similar deal size and match to either the revenue or EBITDA range. Deals that match to
								the industry are listed first, followed by deals in other industries.
							</CerebroText>

							<CerebroText sx={{ paddingRight: 3, paddingLeft: 3, paddingBottom: 3 }}>
								Click the button below to generate a term sheet report with the term sheets from the
								similar deals. You will be redirected to the term sheet report.
							</CerebroText>

							<CerebroSecondaryButton disabled={reportGenerating} onClick={() => reportMutation.mutate()}>
								Generate Term Sheet Report
							</CerebroSecondaryButton>

							<CerebroHeading size="h2" sx={{ marginTop: 3 }}>
								Download Report
							</CerebroHeading>
							<Formik
								validate={validateForm}
								onSubmit={FormikUtils.MutateOnSubmit(downloadMutation.mutate)}
								initialValues={
									{
										includeSummary: true,
										includeIndustry: true,
										includeOther: true,
									} as DownloadSimilarDealsFormValues
								}
							>
								{({ touched, errors, handleSubmit }) => (
									<CerebroForm
										flatIndicator
										onSubmit={handleSubmit}
										sx={{ paddingRight: 3, minWidth: { md: 500 } }}
									>
										<CerebroLabel htmlFor="includeSummary" sx={{ marginTop: 3 }}>
											Include Summary Table
										</CerebroLabel>
										<Field
											small
											id="includeSummary"
											name="includeSummary"
											as={CerebroYesNo}
											error={FormikUtils.IsInvalid('includeSummary', errors, touched)}
										/>
										{FormikUtils.IsInvalid('includeSummary', errors, touched) && (
											<CerebroText variant="error">{errors.includeSummary}</CerebroText>
										)}

										<CerebroLabel htmlFor="includeIndustry" sx={{ marginTop: 3 }}>
											Include Industry Matches
										</CerebroLabel>
										<Field
											small
											id="includeIndustry"
											name="includeIndustry"
											as={CerebroYesNo}
											error={FormikUtils.IsInvalid('includeIndustry', errors, touched)}
										/>
										{FormikUtils.IsInvalid('includeIndustry', errors, touched) && (
											<CerebroText variant="error">{errors.includeIndustry}</CerebroText>
										)}

										<CerebroLabel htmlFor="includeOther" sx={{ marginTop: 3 }}>
											Include Other Industry Matches
										</CerebroLabel>
										<Field
											small
											id="includeOther"
											name="includeOther"
											as={CerebroYesNo}
											error={FormikUtils.IsInvalid('includeOther', errors, touched)}
										/>
										{FormikUtils.IsInvalid('includeOther', errors, touched) && (
											<CerebroText variant="error">{errors.includeOther}</CerebroText>
										)}

										<Grid container spacing={3}>
											<Grid item xs={6}>
												<CerebroPrimaryButton
													loading={downloadReport}
													type="submit"
													sx={{ marginTop: 4 }}
												>
													Download Report
												</CerebroPrimaryButton>
											</Grid>
										</Grid>
									</CerebroForm>
								)}
							</Formik>
						</TabPanel>
					</>
				)}
			</TabContext>
		</CerebroPageContainer>
	);
};

interface SimilarDealsReportResponse {
	termSheetReportHeaderGuid: string;
	createdOn: Date;
}

interface SimilarSummaryTableProps {
	matched: SimilarDealSummary;
	other: SimilarDealSummary;
}

const SimilarSummaryTable: React.FC<SimilarSummaryTableProps> = ({ matched, other }) => {
	return (
		<TableContainer sx={{ marginBottom: 3 }}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Section</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>Deals Launched</TableCell>
						<TableCell sx={{ textAlign: 'center' }}># Term Sheets</TableCell>
						<TableCell sx={{ textAlign: 'center' }}># Closed</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>Funded Amount</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>Average Days to Term Sheet</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					<TableRow>
						<TableCell>Industry Matches</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>{matched.dealsLaunched}</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>{matched.termSheetsReceived}</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>{matched.totalClosed}</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>
							{NumberUtils.FormatNumber(matched.totalFundedAmount, 'abbreviation')}
						</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>
							{matched.averageLaunchToTermSheetDays.toFixed(0)}
						</TableCell>
					</TableRow>
					<TableRow>
						<TableCell>Other Industry Matches</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>{other.dealsLaunched}</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>{other.termSheetsReceived}</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>{other.totalClosed}</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>
							{NumberUtils.FormatNumber(other.totalFundedAmount, 'abbreviation')}
						</TableCell>
						<TableCell sx={{ textAlign: 'center' }}>
							{other.averageLaunchToTermSheetDays.toFixed(0)}
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
		</TableContainer>
	);
};

interface SimilarDealTableProps {
	items: DealSearchItem[];
}

const SimilarDealTable: React.FC<SimilarDealTableProps> = ({ items }) => {
	return (
		<TableContainer sx={{ marginBottom: 3 }}>
			<Table>
				<TableHead>
					<TableRow>
						<TableCell>Company Name</TableCell>
						<TableCell>Industry</TableCell>
						<TableCell>TTM Revenue</TableCell>
						<TableCell>TTM EBITDA</TableCell>
						<TableCell>Deal Size</TableCell>
						<TableCell># Term Sheets</TableCell>
						<TableCell># Lenders Accepted</TableCell>
						<TableCell>Deal Status</TableCell>
						<TableCell>Deal Start Date</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{items.map(related => (
						<TableRow key={related.id}>
							<TableCell>
								<CerebroLink href={`${LoanApplicationUrl}/${related.guid}`} target="_blank">
									{related.companyName}
								</CerebroLink>
							</TableCell>
							<TableCell>{related.industryName}</TableCell>
							<TableCell>{NumberUtils.FormatNumber(related.ttmRevenue, 'abbreviation')}</TableCell>
							<TableCell>{NumberUtils.FormatNumber(related.ttmEbitda, 'abbreviation')}</TableCell>
							<TableCell>{NumberUtils.FormatNumber(related.dealSize, 'abbreviation')}</TableCell>
							<TableCell>{related.totalTermSheets}</TableCell>
							<TableCell>{related.institutionLendersAccepted}</TableCell>
							<TableCell>{EnumUtils.GetDealStatusDescription(related.dealStatus)}</TableCell>
							<TableCell>{DateUtils.FormatStringAsShortDate(related.dealStartDate)}</TableCell>
						</TableRow>
					))}

					{items.length == 0 && (
						<TableRow>
							<TableCell colSpan={9} sx={{ textAlign: 'center' }}>
								<CerebroText sx={{ paddingTop: 3, paddingBottom: 3 }}>
									There are currently no related deals.
								</CerebroText>
							</TableCell>
						</TableRow>
					)}
				</TableBody>
			</Table>
		</TableContainer>
	);
};

interface DownloadSimilarDealsFormValues {
	includeSummary: boolean;
	includeIndustry: boolean;
	includeOther: boolean;
}

type DownloadSimilarDealsFormValuesErrors = FormikErrors<DownloadSimilarDealsFormValues>;
type DownloadSimilarDealsMutationVariables = FormikUtils.MutateOnSubmitVariables<DownloadSimilarDealsFormValues>;

function validateForm(values: DownloadSimilarDealsFormValues) {
	const errors: DownloadSimilarDealsFormValuesErrors = {};

	if (!values.includeIndustry && !values.includeOther && !values.includeSummary) {
		errors.includeSummary = 'You must include at least one section of the report.';
		errors.includeIndustry = 'You must include at least one section of the report.';
		errors.includeOther = 'You must include at least one section of the report.';
	}

	return errors;
}
